/**
 * Turning an activity row into something a human reads.
 *
 * The log stores machine event names (`task.journal_appended`). Printing those
 * at the user is the failure mode this module exists to prevent: the partner
 * block is meant to answer "what changed while I was away", and a column of
 * dotted identifiers answers nothing.
 *
 * Unknown events fall back to a phrase built from the entity half, so a new
 * server-side event type degrades to "изменил задачу" rather than leaking its
 * identifier or vanishing from the feed.
 */

import type { ActivityOut } from "@/api/types";

const VERB: Record<string, string> = {
  "task.created": "создал задачу",
  // PC audit 28.09, point 19: an edit of the task read «задача · изменилась».
  "task.updated": "изменил задачу",
  "task.result_prepared": "подготовил результат",
  "task.result_reviewed": "проверил результат",
  "task.memory_saved": "сохранил опыт в метод",
  "task.delegation_granted": "выдал доступ агенту",
  "task.delegation_revoked": "отозвал доступ агента",
  "task.completed": "завершил задачу",
  "task.reopened": "вернул задачу в работу",
  "task.assigned": "назначил задачу",
  "task.status_changed": "сменил статус задачи",
  "task.journal_appended": "записал в журнал",
  "task.journal_annotated": "пометил запись журнала",
  "task.tools_used_changed": "обновил инструменты задачи",
  "task.deleted": "удалил задачу",
  // Spec 014: the record of an erasure, never the erased text.
  "task.revision_redacted": "стёр текст правки задачи",
  // Spec 017: links between tasks.
  "task.link_added": "связал задачи",
  "task.link_removed": "снял связь задач",
  "task.blocker_closed": "закрыл задачу, которую ждали",
  // Spec 018: passports and moving tasks.
  "task.moved": "перенёс задачу в другой проект",
  "business.updated": "обновил паспорт бизнеса",
  "business.checked": "проверил паспорт бизнеса",
  "project.passport_checked": "проверил паспорт проекта",
  // Spec 018 part B: resources and accesses; the log never holds a secret.
  "resource.created": "описал ресурс",
  "resource.updated": "поправил карточку ресурса",
  "resource.archived": "убрал ресурс в архив",
  "resource.access_granted": "записал доступ к ресурсу",
  "resource.access_updated": "поправил запись о доступе",
  "resource.access_revoked": "отметил, что доступ отозван",
  "resource.access_withdrawn": "снял ошибочную запись о доступе",
  // Spec 018 part C: the secrets store; the log names a secret, never its value.
  "resource.secret_created": "сохранил секрет",
  "resource.secret_updated": "поправил описание секрета",
  "resource.secret_rotated": "сменил значение секрета",
  "resource.secret_destroyed": "удалил секрет",
  // Spec 025: cases and where each one was used.
  "case.created": "написал черновик кейса",
  "case.updated": "поправил кейс",
  "case.submitted": "отправил кейс на проверку",
  "case.accepted": "принял кейс",
  "case.returned": "вернул кейс на доработку",
  "case.archived": "убрал кейс в архив",
  "case.restored": "вернул кейс из архива",
  "case.used": "записал, где использован кейс",
  "case.use_deleted": "удалил запись об использовании кейса",
  "case_place.created": "добавил площадку для кейсов",
  // Spec 027: agents' cards and who approved which version.
  "agent.created": "создал агента",
  "agent.updated": "изменил карточку агента",
  "agent.approved": "одобрил версию агента",
  "agent.archived": "убрал агента в архив",
  "agent.restored": "вернул агента из архива",
  "resource.secret_granted": "выдал секрет",
  "resource.secret_revoked": "отозвал выдачу секрета",
  "idea.created": "закинул идею",
  "idea.updated": "поправил идею",
  "idea.merged": "дополнил идею",
  "idea.rejected": "отклонил идею",
  "idea.withdrawn": "отозвал свою идею",
  "idea.returned": "вернул идею в обсуждение",
  "idea.converted": "превратил идею в задачу",
  "idea.opened_for_review": "взял идею на ревью",
  "idea.project_bound": "привязал идею к проекту",
  "project.created": "создал проект",
  "project.updated": "поправил проект",
  "project.area_changed": "сменил область проекта",
  "project.status_added": "добавил статус в проект",
  "project.status_removed": "убрал статус из проекта",
  "note.created": "написал заметку",
  "note.updated": "поправил заметку",
  "note.promoted_to_task": "превратил заметку в задачу",
  "note.promoted_to_idea": "превратил заметку в идею",
  "comment.created": "оставил комментарий",
  "comment.edited": "поправил комментарий",
  "attachment.uploaded": "приложил файл",
  "attachment.reassigned": "перенёс вложение",
  "tool.created": "зарегистрировал инструмент",
  "tool.updated": "обновил инструмент",
  "tool.archived": "заархивировал инструмент",
  "area.created": "создал область",
  "area.updated": "поправил область",
  "area.archived": "заархивировал область",
};

// What was changed, as the object of «изменил …».
const ENTITY: Record<string, string> = {
  task: "задачу",
  idea: "идею",
  project: "проект",
  note: "заметку",
  comment: "комментарий",
  tool: "инструмент",
  area: "область",
  business: "паспорт бизнеса",
  resource: "ресурс",
  // Spec 025.
  case: "кейс",
  case_place: "площадка кейсов",
  // Spec 027.
  agent: "агента",
};

export function activityLabel(row: ActivityOut): string {
  const known = VERB[row.type];
  if (known) return known;
  const entity = ENTITY[row.entity_type] ?? ENTITY[row.type.split(".")[0]];
  return entity ? `изменил ${entity}` : "изменение";
}

/**
 * Where tapping a row should go, or null when the entity has no screen of its
 * own. A row that leads nowhere is rendered without a chevron rather than as a
 * dead tap target.
 */
export function activityHref(row: ActivityOut): string | null {
  switch (row.entity_type) {
    case "task":
      return `/tasks/${row.entity_id}`;
    case "project":
      return `/projects/${row.entity_id}`;
    case "idea":
      return "/ideas";
    case "note":
      return "/today";
    case "tool":
      return "/tools";
    // Spec 018: the business passport and the resources live on «О бизнесе».
    case "business":
    case "resource":
      return "/business";
    case "case":
      return `/knowledge/cases/${row.entity_id}`;
    case "case_place":
      return "/knowledge/cases";
    case "agent":
      return `/agents/${row.entity_id}`;
    default:
      return null;
  }
}
